import { ChangeEvent, useEffect, useState } from 'react';
import { Grid } from '@mui/system';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { CompanyFiscalYear } from 'api';
import { RELATED_ENTITY } from 'common/constants/companies';
import { CustomTextField, WebsiteTextField } from 'components';
import CurrencyList from 'components/CurrencyList';
import { CurrencyListOption } from 'components/CurrencyList/CurrencyList';
import { useFormValidation } from 'services/hooks/useFormValidation';
import { getLocaleAgnosticDate, reconstructDate } from 'utilities';
import { CompanyFormProps, FormValueCompanyForm } from './CompanyForm.props';
import { classes, StyledGrid, StyledGridDatePicker } from './CompanyForm.styles';
import OtherNames from './OtherNames';

const constraints = {
  name: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { minimum: 3, message: 'must be at least 3 characters' },
  },
  website: {
    url: true,
  },
};

const CompanyForm = ({
  defaultFormState,
  onFormChange,
  editMode,
  initialValues,
  isRelatedEntity = false,
}: CompanyFormProps) => {
  const { values, errors, dbErrors } = defaultFormState;
  const { formValidated, validateForm } = useFormValidation(constraints);
  const [formValues, setFormValues] = useState<FormValueCompanyForm>(editMode ? initialValues : values);

  const initialFiscalYearEnd = editMode
    ? reconstructDate(
        (initialValues.fiscal_year_end?.end_month ?? '') as string,
        (initialValues.fiscal_year_end?.end_day ?? '') as string
    )
    : reconstructDate();
  const [selectedDate, setSelectedDate] = useState<string>(initialFiscalYearEnd);
  /* default date when creating company should be December 31. If there's no change here,
  it will not send any fiscal year data in the form values and backend will use default. */

  // in EditMode should pre populate date with stored value
  useEffect(() => {
    validateForm(formValues);
  }, [formValues, validateForm]);

  useEffect(() => {
    onFormChange(formValidated);
  }, [formValidated, onFormChange]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Locale-agnostic date
      const { month, day, year } = getLocaleAgnosticDate({ date });
      const formattedDate = `${month}/${day}/${year}`;
      const fiscalYearEnd: CompanyFiscalYear = {
        ...formValues.fiscal_year_end,
        end_month: Number(month),
        end_day: Number(day),
      };
      setSelectedDate(formattedDate);
      handleChange({
        target: {
          name: 'fiscal_year_end',
          value: fiscalYearEnd,
        },
      } as ChangeEvent<HTMLInputElement>);
    }
  };

  const handleCurrencyChange = (event: ChangeEvent<unknown>, option: CurrencyListOption | null) => {
    const {id} = (event.target as HTMLInputElement);
    let fieldName: keyof FormValueCompanyForm | undefined;
    if (id.includes('irr-currency')) {
      fieldName = 'irr_currency';
    } else if (id.includes('captable-currency')) {
      fieldName = 'captable_currency';
    } else if (id.includes('financials-currency')) {
      fieldName = 'financials_currency';
    }
    if (!fieldName) {
      return;
    }
    // option=null fires on the clear (X) affordance; emit '' so the
    // serializer's `_NullableIRRCurrencyField` round-trips back to NULL.
    handleChange({
      target: {
        name: fieldName,
        value: option?.value ?? '',
      },
    } as ChangeEvent<HTMLInputElement>);
  };

  if (isRelatedEntity) {
    return (
      <StyledGrid container display="block">
        <Grid container spacing={2} justifyContent="space-between">
          <StyledGrid size={{ xs: 12 }}>
            <CustomTextField
              fullWidth
              required
              id="related-entity-name"
              name="name"
              type="text"
              label={RELATED_ENTITY.NAME_LABEL}
              className={classes.marginBottom}
              value={formValues.name ?? ''}
              onChange={handleChange}
              formErrors={errors}
              dbErrors={dbErrors}
            />
          </StyledGrid>
        </Grid>
      </StyledGrid>
    );
  }

  return (
    <Grid container display="block">
      <Grid container spacing={2} justifyContent="space-between">
        <StyledGrid size={{ xs: 8 }}>
          <CustomTextField
            fullWidth
            required
            id="company-name"
            name="name"
            type="text"
            label="Name"
            className={classes.marginBottom}
            value={formValues.name ?? ''}
            onChange={handleChange}
            formErrors={errors}
            dbErrors={dbErrors}
            variant="standard"
          />
        </StyledGrid>
        <StyledGridDatePicker size={{ xs: 4 }}>
          <MobileDatePicker
            openTo={editMode ? 'day' : 'month'}
            format="MM/dd"
            label="FY End"
            slotProps={{
              textField: {
                required: true,
              },
            }}
            views={['year', 'month', 'day']}
            value={new Date(selectedDate)}
            onChange={handleDateChange}
          />
        </StyledGridDatePicker>
      </Grid>
      <Grid container spacing={1}>
        <StyledGrid size={{ xs: 8 }}>
          <CustomTextField
            fullWidth
            id="legal-company-name"
            name="legal_company_name"
            type="text"
            label="Legal Company Name"
            className={classes.marginBottom}
            value={formValues.legal_company_name ?? ''}
            onChange={handleChange}
            formErrors={errors}
            dbErrors={dbErrors}
            variant="standard"
          />
        </StyledGrid>
        <StyledGrid size={{ xs: 4 }} className={classes.marginBottom}>
          <CurrencyList
            label="Captable Currency"
            formCurrency={formValues.captable_currency}
            handleCurrencyChange={handleCurrencyChange}
            variant="standard"
          />
        </StyledGrid>
      </Grid>
      {editMode && initialValues.id !== undefined && <OtherNames companyId={initialValues.id} />}
      <Grid container spacing={1}>
        <StyledGrid size={{ xs: 8 }}>
          <WebsiteTextField
            fullWidth
            id="company-website"
            className={classes.marginBottom}
            name="website"
            type="text"
            label="Website"
            value={formValues.website ?? ''}
            onChange={handleChange}
            formErrors={errors}
            dbErrors={dbErrors}
            variant="standard"
          />
        </StyledGrid>
        <StyledGrid size={{ xs: 4 }} className={classes.marginBottom}>
          <CurrencyList
            label="Financials Currency"
            formCurrency={formValues.financials_currency}
            handleCurrencyChange={handleCurrencyChange}
            variant="standard"
          />
        </StyledGrid>
      </Grid>
      <Grid container spacing={1}>
        <StyledGrid size={{ xs: 8 }} />
        <StyledGrid size={{ xs: 4 }} className={classes.marginBottom}>
          <CurrencyList
            label="IRR Currency"
            formCurrency={formValues.irr_currency}
            handleCurrencyChange={handleCurrencyChange}
            variant="standard"
            clearable
            helperText={
              formValues.irr_currency
                ? undefined
                : `Defaults to ${formValues.captable_currency || 'cap table currency'}`
            }
          />
        </StyledGrid>
      </Grid>
    </Grid>
  );
};

export default CompanyForm;
